import { ProductType } from '@features/products/enums/productType'
import type { ProductUnit } from '@features/products/types/productUnit'
import { getConfiguredUnitByValue } from '@features/products/utils/getConfiguredUnitByValue'
import { getProductUnitCode } from '@features/products/utils/getProductUnitCode'
import { getProductUnitName } from '@features/products/utils/getProductUnitName'

import { convertDateToUTC, NumberValue } from '../../../../utils'
import i18n from '../../../../utils/i18n'
import { InvoiceLineType } from '../../enums/invoiceLineType'
import { InvoiceForm } from '../../types/invoiceForm/invoiceForm'
import type { InvoiceFormLine } from '../../types/invoiceForm/invoiceFormLine/invoiceFormLine'
import { InvoicePayloadLine } from '../../types/invoicePayload/invoicePayloadLine'
import { getSignedAmount } from '../getSignedAmount'
import { InvoiceDiscountCalculator } from '../invoiceDiscountCalculator'
import { isInvoiceFormRowLine } from '../isInvoiceFormRowLine'

export const getLinesPayload = (
  formValues: InvoiceForm,
  units: ProductUnit[],
  isCreditNote?: boolean,
): InvoicePayloadLine[] => {
  return formValues.invoiceLines.flatMap((line: InvoiceFormLine): InvoicePayloadLine[] => {
    if (isInvoiceFormRowLine(line)) {
      const {
        date,
        productId,
        discount,
        productName,
        productType,
        quantity,
        type,
        unit,
        unitPrice,
        vatRatePercent,
        vatRateId,
      } = line

      // Uses this line's own `discount` field rather than getSummaryPayload's already-computed
      // calculateInvoice result: in Global discount mode, invoice lines stay undiscounted on purpose
      // (only the summary reflects the discount), so reusing calculateInvoice's distributed share
      // here would apply the global discount to the line totals.
      const {
        discountAmount: totalDiscount,
        totalExcludingVat,
        totalIncludingVat,
      } = InvoiceDiscountCalculator.calculateLine(
        { discount, quantity, unitPrice, vatRatePercent },
        formValues.discountType,
        formValues.currency,
      )

      const matchedUnit = getConfiguredUnitByValue(units, unit)

      const grossTotal = getSignedAmount(totalIncludingVat, isCreditNote)
      const netTotal = getSignedAmount(totalExcludingVat, isCreditNote)
      const taxTotal = new NumberValue(grossTotal).subtract(netTotal).getRounded()

      return [
        {
          currency: formValues.currency,
          date: date ? convertDateToUTC(date).toISOString() : convertDateToUTC(new Date()).toISOString(),
          description: productName,
          discount,
          grossTotal,
          netTotal,
          productId: productId || undefined,
          productType,
          quantity,
          taxTotal,
          totalDiscount,
          type,
          // The form stores the abbreviation in the app language; the document follows the invoice language
          unit: matchedUnit ? getProductUnitName(matchedUnit, i18n.t, formValues.language) : unit,
          unitCode: getProductUnitCode(matchedUnit),
          unitPrice,
          vatPct: vatRatePercent,
          vatRateId,
        },
      ]
    }

    if (line.type === InvoiceLineType.Description) {
      const { content, type } = line
      return [
        {
          content,
          type,
        },
      ]
    }

    if (line.type === InvoiceLineType.Deposit) {
      const { description, totalExcludingVat, totalIncludingVat, vatRatePercent, vatRateId } = line

      const grossTotal = new NumberValue(totalIncludingVat).getRounded()
      const netTotal = new NumberValue(totalExcludingVat).getRounded()
      const taxTotal = new NumberValue(grossTotal).subtract(netTotal).getRounded()

      return [
        {
          currency: formValues.currency,
          date: formValues.issueDate
            ? convertDateToUTC(formValues.issueDate).toISOString()
            : convertDateToUTC(new Date()).toISOString(),
          description,
          discount: 0,
          grossTotal,
          netTotal,
          productType: ProductType.Services,
          quantity: 1,
          taxTotal,
          totalDiscount: 0,
          type: InvoiceLineType.Row,
          unit: '',
          unitCode: null,
          unitPrice: totalExcludingVat,
          vatPct: vatRatePercent,
          vatRateId,
        },
      ]
    }

    if (line.type === InvoiceLineType.Subtotal) {
      const { subtotalIncludingVat, subtotalExcludingVat } = line
      const grossTotal = new NumberValue(subtotalIncludingVat).getRounded()
      const netTotal = new NumberValue(subtotalExcludingVat).getRounded()

      return [
        {
          grossTotal,
          netTotal,
          showGrossTotal: true,
          showNetTotal: true,
          showTaxTotal: false,
          showProductType: false,
          taxTotal: 0,
          title: 'Subtotal',
          type: line.type,
        },
      ]
    }

    return []
  })
}
